import {
  HomeIcon,
  CloudArrowUpIcon,
  DocumentTextIcon,
  BookOpenIcon,
  QuestionMarkCircleIcon,
  ClipboardIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  TableCellsIcon,
  LightBulbIcon,
  MapPinIcon,
  CalculatorIcon,
  PaperAirplaneIcon,
  ChartBarSquareIcon,
  ScaleIcon,
  DocumentMagnifyingGlassIcon,
  UserIcon,
  MapIcon,
  ShieldExclamationIcon,
  ClipboardDocumentCheckIcon,
  NewspaperIcon,
  ExclamationTriangleIcon,
  BuildingLibraryIcon,
  CalendarDaysIcon,
  DocumentChartBarIcon,
  Cog6ToothIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  PuzzlePieceIcon,
  CreditCardIcon,
  ChatBubbleLeftEllipsisIcon,
  BuildingOfficeIcon,
  CpuChipIcon,
  CubeIcon,
  MegaphoneIcon,
  AcademicCapIcon,
  DocumentPlusIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

import type { TeamRole } from '../../lib/roles';

export interface NavItem {
  name: string;
  href: string;
  icon: React.ForwardRefExoticComponent<
    React.SVGProps<SVGSVGElement> & { title?: string; titleId?: string }
  >;
  badge?: 'pending-reviews';
  /** Feature key required to see this item. If absent, item is always visible. */
  requiredFeature?: string;
  /** Team roles allowed to see this item. If absent, all roles can see it.
   *  Used for sensitive admin items that VIEWER / MARKETING_* should not see
   *  (Billing, Teams, Settings, Audit Logs). */
  requiredRoles?: TeamRole[];
}

export interface NavSection {
  id: string;
  label: string;
  icon: NavItem['icon'];
  items: NavItem[];
  /** Feature key required to see this entire section. If absent, section is always visible. */
  requiredFeature?: string;
}

export const navSections: NavSection[] = [
  {
    id: 'marketing-compliance',
    label: 'Marketing',
    icon: ShieldCheckIcon,
    items: [
      { name: 'Content Review', href: '/upload', icon: CloudArrowUpIcon },
      { name: 'Reports', href: '/reports', icon: DocumentTextIcon },
      { name: 'Content Library', href: '/content-library', icon: BookOpenIcon },
      { name: 'Approval Workflow', href: '/pending-reviews', icon: ClipboardIcon, badge: 'pending-reviews' },
      { name: 'Website Scan', href: '/website-scan', icon: GlobeAltIcon },
      { name: 'Social Media', href: '/social-media', icon: MegaphoneIcon },
      { name: 'MCP Checks', href: '/mcp-checks', icon: ShieldCheckIcon },
      { name: 'Analytics', href: '/analytics', icon: ChartBarIcon },
    ],
  },
  {
    id: 'hmda',
    label: 'HMDA',
    icon: TableCellsIcon,
    items: [
      { name: 'LAR Builder', href: '/hmda/lar-builder', icon: DocumentPlusIcon, requiredFeature: 'module_hmda_lar' },
      { name: 'LO Field Guide', href: '/hmda/field-guide', icon: ClipboardDocumentListIcon, requiredFeature: 'module_hmda_lar' },
      { name: 'LAR Validation', href: '/hmda/lar-validation', icon: TableCellsIcon },
      { name: 'Edit Intelligence', href: '/hmda/edit-intelligence', icon: LightBulbIcon, requiredFeature: 'edit_intelligence' },
      { name: 'Geocoding Validation', href: '/hmda/geocoding', icon: MapPinIcon, requiredFeature: 'geocoding_validation' },
      { name: 'APOR Rate Spread', href: '/hmda/apor', icon: CalculatorIcon, requiredFeature: 'apor_rate_spread' },
      { name: 'CFPB Filing', href: '/hmda/cfpb-filing', icon: PaperAirplaneIcon, requiredFeature: 'cfpb_filing' },
      { name: 'Data Quality', href: '/hmda/quality', icon: ChartBarSquareIcon },
    ],
  },
  {
    id: 'fair-lending',
    label: 'Fair Lending',
    icon: ScaleIcon,
    requiredFeature: 'fair_lending',
    items: [
      { name: 'Denial & Pricing', href: '/fair-lending', icon: ScaleIcon },
      { name: 'Loan Trial Balance (ALERT/ILDR)', href: '/alert/upload', icon: CloudArrowUpIcon },
      { name: 'Risk Assessments', href: '/fair-lending/risk-assessments', icon: DocumentMagnifyingGlassIcon },
      { name: 'LO Monitoring', href: '/fair-lending/loan-officers', icon: UserIcon },
      { name: 'Redlining Analysis', href: '/fair-lending/redlining', icon: MapIcon },
      { name: 'Redlining Map', href: '/fair-lending/redlining-map', icon: MapPinIcon },
      { name: 'Regression Analysis', href: '/fair-lending/regression', icon: ChartBarSquareIcon },
      { name: 'Content Screening', href: '/fair-lending/content-findings', icon: ShieldExclamationIcon },
      { name: 'Exception Tracking', href: '/exceptions', icon: ShieldExclamationIcon },
    ],
  },
  {
    id: 'cra-compliance',
    label: 'CRA Compliance',
    icon: BuildingLibraryIcon,
    requiredFeature: 'module_cra',
    items: [
      { name: 'CRA Dashboard', href: '/cra', icon: ChartBarSquareIcon },
      { name: 'Assessment Areas', href: '/cra/assessment-areas', icon: MapPinIcon },
      { name: 'SB Loans', href: '/cra/sb-loans', icon: CubeIcon },
      { name: 'Lending Analysis', href: '/cra/lending-analysis', icon: ScaleIcon },
      { name: 'Lending Test', href: '/cra/lending-test', icon: ClipboardDocumentCheckIcon },
      // The Large Bank CRAD had a route and no way in (2026-10-15).
      { name: 'Large Bank CRAD', href: '/cra/large-bank', icon: BuildingLibraryIcon },
      { name: 'CD Activities', href: '/cra/cd-activities', icon: BuildingOfficeIcon },
      { name: 'Gap Analysis', href: '/cra/gap-analysis', icon: DocumentMagnifyingGlassIcon },
      { name: 'Public File', href: '/cra/public-file', icon: DocumentChartBarIcon },
    ],
  },
  {
    id: 'bsa-aml',
    label: 'BSA / AML',
    icon: ShieldCheckIcon,
    // Separate SKU (CLARID-SPEC-BSA-001 §2.1) — its own feature, not bundled
    // into CRA or Fair Lending entitlements.
    requiredFeature: 'module_bsa',
    items: [
      { name: 'Risk Assessment', href: '/bsa/assessments', icon: ShieldCheckIcon },
      { name: 'Obligation Register', href: '/bsa/obligations', icon: ClipboardDocumentCheckIcon },
    ],
  },
  {
    id: 'mapping',
    label: 'Mapping',
    icon: MapIcon,
    items: [
      // Cross-module unified map (CLARID-UX-2026-06). Page is always visible;
      // individual layers gate by feature inside the page.
      { name: 'Map Explorer', href: '/mapping', icon: MapIcon },
    ],
  },
  {
    id: 'branches',
    label: 'Branches',
    icon: MapPinIcon,
    items: [
      { name: 'All Branches', href: '/branches', icon: MapPinIcon },
    ],
  },
  {
    id: 'exam-center',
    label: 'Exam Center',
    icon: ClipboardDocumentCheckIcon,
    items: [
      { name: 'Exam Readiness', href: '/exam-readiness', icon: ChartBarSquareIcon },
      { name: 'Exam Management', href: '/exams', icon: ClipboardDocumentCheckIcon, requiredFeature: 'exam_management' },
      { name: 'Compliance Hub', href: '/compliance-hub', icon: ShieldCheckIcon, requiredFeature: 'exam_package' },
      { name: 'Comprehensive Exam Package', href: '/packages', icon: DocumentChartBarIcon, requiredFeature: 'exam_package' },
    ],
  },
  {
    id: 'regulatory-intelligence',
    label: 'Regulatory Intelligence',
    icon: NewspaperIcon,
    items: [
      { name: 'Regulatory Changes', href: '/regulatory-intelligence', icon: NewspaperIcon },
      { name: 'Enforcement Intelligence', href: '/enforcement-intelligence', icon: ExclamationTriangleIcon, requiredFeature: 'enforcement_intelligence' },
      { name: 'State-Specific Rules', href: '/state-compliance', icon: BuildingLibraryIcon },
    ],
  },
  {
    id: 'compliance-program',
    label: 'Compliance Program',
    icon: CalendarDaysIcon,
    items: [
      { name: 'Calendar & Tasks', href: '/calendar', icon: CalendarDaysIcon },
      { name: 'Board Reporting', href: '/board-reports', icon: DocumentChartBarIcon },
      { name: 'Peer Benchmarking', href: '/peer-benchmarking', icon: ChartBarIcon, requiredFeature: 'peer_benchmarking' },
      { name: 'Policies', href: '/policies', icon: DocumentTextIcon },
      { name: 'Procedure Review', href: '/procedures', icon: ClipboardDocumentListIcon },
      { name: 'Complaints', href: '/complaints', icon: ChatBubbleLeftEllipsisIcon },
      { name: 'Vendor Compliance', href: '/vendor-compliance', icon: BuildingOfficeIcon, requiredFeature: 'vendor_compliance' },
      { name: 'Training', href: '/training', icon: AcademicCapIcon, requiredFeature: 'training_management' },
      // Audit Logs lives here (not the footer admin area) so Compliance
      // Officers — who reasonably need to find audit logs to demo to
      // examiners — actually see it. Restricted to OWNER + COMPLIANCE_OFFICER
      // since other roles shouldn't see audit data.
      {
        name: 'Audit Logs',
        href: '/audit-logs',
        icon: ClipboardDocumentListIcon,
        requiredRoles: ['OWNER', 'COMPLIANCE_OFFICER'],
      },
    ],
  },
  {
    id: 'platform',
    label: 'Platform',
    icon: CpuChipIcon,
    items: [
      { name: 'Knowledge Base', href: '/regulations', icon: BookOpenIcon },
      { name: 'Product Profiles', href: '/product-profiles', icon: CubeIcon },
      { name: 'Campaigns', href: '/campaigns', icon: MegaphoneIcon },
      { name: 'Custom Rules', href: '/custom-rules', icon: PuzzlePieceIcon, requiredFeature: 'custom_rules' },
      { name: 'AI Performance', href: '/ai-performance', icon: ChartBarSquareIcon, requiredFeature: 'ai_performance' },
    ],
  },
];

export const dashboardItem: NavItem = {
  name: 'Dashboard',
  href: '/dashboard',
  icon: HomeIcon,
};

/** The Clarid Agent (Ask Clarid). Always visible, under Dashboard (CLARID-SPEC-AGENT-001). */
export const agentItem: NavItem = {
  name: 'Clarid Agent',
  href: '/agent',
  icon: SparklesIcon,
};

/**
 * Organization-level entry point for users who manage multiple banks/CUs
 * (consultants, channel partners, holding-company admins). Conditionally
 * rendered by SidebarLayout: only appears when the signed-in user has access
 * to >1 team. Single-bank users — the majority — never see it.
 *
 * Lives ABOVE Dashboard in the sidebar so org-level users start their workflow
 * at the right altitude: "which bank am I working on today?" → drill down.
 */
export const organizationItem: NavItem = {
  name: 'Organization',
  href: '/organization',
  icon: BuildingOfficeIcon,
};

export const footerItems: NavItem[] = [
  // Help is for everyone — no role gate. Kept first so it's easy to find.
  {
    name: 'How-To & Glossary',
    href: '/how-to',
    icon: QuestionMarkCircleIcon,
  },
  // Sensitive admin items — restricted to OWNER (and COMPLIANCE_OFFICER for
  // audit logs, since reviewing audit logs is part of compliance work).
  // QA #3 #1 explicitly flagged that VIEWER seeing the Billing page exposing
  // pricing/plan info to a read-only employee was the most embarrassing
  // finding in the report. These role gates fix the same class for Settings,
  // Teams, Audit Logs, and Integrations.
  {
    name: 'Settings',
    href: '/settings',
    icon: Cog6ToothIcon,
    requiredRoles: ['OWNER'],
  },
  {
    // "Teams" in the data model is the tenant entity, but every Clarid
    // tenant represents a bank or credit union. The user-facing label
    // says so directly; the URL stays /teams to preserve link stability.
    name: 'Banks & Credit Unions',
    href: '/teams',
    icon: UserGroupIcon,
    requiredRoles: ['OWNER'],
  },
  // Audit Logs moved out of the footer admin area into the Compliance
  // Program section so Compliance Officers actually see it.
  {
    name: 'Integrations',
    href: '/integrations',
    icon: PuzzlePieceIcon,
    requiredRoles: ['OWNER'],
  },
  {
    name: 'Billing',
    href: '/billing',
    icon: CreditCardIcon,
    requiredRoles: ['OWNER'],
  },
];

/** One entry in the consolidated "Unlock more" upsell surface (UX epic PR 7). */
export interface LockedNavEntry {
  key: string;
  name: string;
  icon: NavItem['icon'];
  /** Feature key that unlocks it — handed to UpgradePrompt on click. */
  feature: string;
}

export interface TieredNav {
  /** Sections to render, tier-ordered, each carrying only feature-accessible items. */
  sections: { section: NavSection; items: NavItem[] }[];
  /** Everything the plan lacks — whole locked sections AND individually locked
   *  items — consolidated for the single "Unlock more" upsell surface. */
  locked: LockedNavEntry[];
}

/**
 * Tier-aware navigation (UX epic PR 7). Instead of showing every section/item to
 * every plan with scattered inline padlocks, this partitions the nav by what the
 * bank is entitled to:
 *   - feature-accessible items render in their normal functional sections;
 *   - everything locked (whole gated sections AND individual gated items) is
 *     consolidated into ONE "Unlock more" upsell surface;
 *   - the bank's specialty section (Fair Lending / CRA) is hoisted to lead.
 *     A bank with both (Enterprise) or neither (Core) keeps the default order —
 *     Core's Marketing already leads.
 * Role gating is intentionally NOT handled here — it's left to the section
 * renderer, because a role-hidden item should be hidden, never upsold.
 */
export function buildTieredNav(hasFeature: (feature: string) => boolean): TieredNav {
  const sections: TieredNav['sections'] = [];
  const locked: LockedNavEntry[] = [];

  for (const section of navSections) {
    // Whole section gated and not entitled → a single upsell entry for the module.
    if (section.requiredFeature && !hasFeature(section.requiredFeature)) {
      locked.push({
        key: section.id,
        name: section.label,
        icon: section.icon,
        feature: section.requiredFeature,
      });
      continue;
    }
    const accessible: NavItem[] = [];
    for (const item of section.items) {
      if (item.requiredFeature && !hasFeature(item.requiredFeature)) {
        locked.push({ key: item.href, name: item.name, icon: item.icon, feature: item.requiredFeature });
      } else {
        accessible.push(item);
      }
    }
    if (accessible.length > 0) sections.push({ section, items: accessible });
  }

  const leadId =
    hasFeature('module_cra') && !hasFeature('fair_lending')
      ? 'cra-compliance'
      : hasFeature('fair_lending') && !hasFeature('module_cra')
        ? 'fair-lending'
        : null;
  if (leadId) {
    const idx = sections.findIndex((s) => s.section.id === leadId);
    if (idx > 0) sections.unshift(sections.splice(idx, 1)[0]);
  }

  return { sections, locked };
}

/**
 * Display-only map: which upgrade tier unlocks each gated feature, used to group
 * the "Unlock more" surface into clear upsell buckets. This is NOT a gating
 * source (featureGate.service.ts is authoritative) — it only labels the cheapest
 * upgrade. Unknown features fall back to Enterprise. Fair Lending and CRA are
 * the same price ($4,995); features available from either are bucketed together.
 */
const FEATURE_UPGRADE_TIER: Record<string, string> = {
  // BSA/AML — separate SKU, own price ladder (docs/PRICING_BSA_MODULE.md)
  module_bsa: 'BSA / AML',
  // Fair Lending
  fair_lending: 'Fair Lending',
  module_fair_lending: 'Fair Lending',
  redlining_analysis: 'Fair Lending',
  risk_assessment_generator: 'Fair Lending',
  exception_tracking: 'Fair Lending',
  // CRA
  module_cra: 'CRA',
  peer_benchmarking: 'CRA',
  // Either specialty tier unlocks these
  geocoding_validation: 'Fair Lending or CRA',
  exam_package: 'Fair Lending or CRA',
  // Enterprise
  module_hmda_lar: 'Enterprise',
  edit_intelligence: 'Enterprise',
  apor_rate_spread: 'Enterprise',
  cfpb_filing: 'Enterprise',
  lo_performance_monitoring: 'Enterprise',
  exam_management: 'Enterprise',
  vendor_compliance: 'Enterprise',
  vendor_risk_management: 'Enterprise',
  custom_rules: 'Enterprise',
};

const UPGRADE_TIER_ORDER = ['Fair Lending', 'CRA', 'Fair Lending or CRA', 'Enterprise'];

export interface UpgradeBucket {
  tier: string;
  entries: LockedNavEntry[];
}

/** Group "Unlock more" entries by the tier that unlocks them, in price order. */
export function groupLockedByTier(locked: LockedNavEntry[]): UpgradeBucket[] {
  const byTier = new Map<string, LockedNavEntry[]>();
  for (const entry of locked) {
    const tier = FEATURE_UPGRADE_TIER[entry.feature] ?? 'Enterprise';
    const bucket = byTier.get(tier);
    if (bucket) bucket.push(entry);
    else byTier.set(tier, [entry]);
  }
  return UPGRADE_TIER_ORDER.filter((t) => byTier.has(t)).map((tier) => ({
    tier,
    entries: byTier.get(tier)!,
  }));
}

/**
 * Compute the active nav href for a given pathname using longest-prefix match.
 * Ensures the most specific item lights up — e.g. `/hmda/edit-intelligence`
 * matches its own item, not a less-specific sibling.
 */
export function getActiveNavHref(pathname: string): string | null {
  const allHrefs: string[] = [
    dashboardItem.href,
    agentItem.href,
    ...navSections.flatMap((s) => s.items.map((i) => i.href)),
    ...footerItems.map((i) => i.href),
  ];
  let best: string | null = null;
  for (const href of allHrefs) {
    if (pathname === href || pathname.startsWith(href + '/')) {
      if (!best || href.length > best.length) best = href;
    }
  }
  return best;
}
